import type { Message } from '../types';

export type AsyncJobStatus = 'processing' | 'completed' | 'failed' | 'cancelled';

export interface AsyncJobMetadata {
  jobId: string;
  status: AsyncJobStatus;
  progressMessage: string;
  startedAt: string;
  elapsedMs: number;
  userMessageId?: string | null;
  assistantMessageId?: string | null;
}

export interface AsyncJobConversationSummary {
  conversationId: string;
  activeJobCount: number;
  jobIds: string[];
  latestJobId: string;
  latestStatus: AsyncJobStatus;
  progressMessage: string;
  startedAt: string;
  elapsedMs: number;
  assistantMessageId?: string | null;
  userMessageId?: string | null;
  updatedAt: string;
}

function getMessageTimestamp(message: Message) {
  return message.createdAt ?? message.timestamp ?? new Date().toISOString();
}

export function getAsyncJobMetadata(message: Message | null | undefined): AsyncJobMetadata | null {
  const candidate = message?.metadata && typeof message.metadata === 'object'
    ? (message.metadata as Record<string, unknown>).asyncJob
    : null;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const jobId = String((candidate as Record<string, unknown>).jobId || '').trim();
  if (!jobId) {
    return null;
  }

  const startedAtValue = (candidate as Record<string, unknown>).startedAt;
  const startedAt = typeof startedAtValue === 'string' && startedAtValue.trim()
    ? startedAtValue
    : getMessageTimestamp(message as Message);
  const elapsedMsValue = Number((candidate as Record<string, unknown>).elapsedMs ?? 0);

  return {
    jobId,
    status: String((candidate as Record<string, unknown>).status || 'processing') as AsyncJobStatus,
    progressMessage: String((candidate as Record<string, unknown>).progressMessage || 'Thinking...'),
    startedAt,
    elapsedMs: Number.isFinite(elapsedMsValue) ? Math.max(0, elapsedMsValue) : 0,
    userMessageId: (candidate as Record<string, unknown>).userMessageId ? String((candidate as Record<string, unknown>).userMessageId) : undefined,
    assistantMessageId: (candidate as Record<string, unknown>).assistantMessageId ? String((candidate as Record<string, unknown>).assistantMessageId) : undefined,
  };
}

export function isAsyncJobActiveStatus(status?: string | null) {
  return String(status || 'processing').toLowerCase() === 'processing';
}

export function isAsyncJobTerminalStatus(status?: string | null) {
  const normalized = String(status || '').toLowerCase();
  return normalized === 'completed' || normalized === 'failed' || normalized === 'cancelled';
}

export function shouldRetainAsyncJobPlaceholderStatus(status?: string | null) {
  const normalized = String(status || 'processing').toLowerCase();
  return normalized === 'processing' || normalized === 'cancelled' || normalized === 'completed';
}

export function getPendingAsyncJobPlaceholderMessages(messages: Message[]) {
  return messages.filter((message) => {
    const asyncJob = getAsyncJobMetadata(message);
    return Boolean(asyncJob && shouldRetainAsyncJobPlaceholderStatus(asyncJob.status));
  });
}

export function buildAsyncJobSummary(messages: Message[], conversationId: string): AsyncJobConversationSummary | null {
  const activeJobs = messages
    .map((message) => ({
      message,
      asyncJob: getAsyncJobMetadata(message),
    }))
    .filter((entry): entry is { message: Message; asyncJob: AsyncJobMetadata } => Boolean(entry.asyncJob && isAsyncJobActiveStatus(entry.asyncJob.status)))
    .sort((left, right) => new Date(left.asyncJob.startedAt).getTime() - new Date(right.asyncJob.startedAt).getTime());

  if (activeJobs.length === 0) {
    return null;
  }

  const latest = activeJobs[activeJobs.length - 1];
  return {
    conversationId,
    activeJobCount: activeJobs.length,
    jobIds: activeJobs.map((entry) => entry.asyncJob.jobId),
    latestJobId: latest.asyncJob.jobId,
    latestStatus: latest.asyncJob.status,
    progressMessage: latest.asyncJob.progressMessage,
    startedAt: latest.asyncJob.startedAt,
    elapsedMs: latest.asyncJob.elapsedMs,
    assistantMessageId: latest.asyncJob.assistantMessageId ?? latest.message.id,
    userMessageId: latest.asyncJob.userMessageId ?? null,
    updatedAt: getMessageTimestamp(latest.message),
  };
}

export function buildAsyncJobSummariesByConversation(messagesByConversation: Record<string, Message[]>) {
  return Object.entries(messagesByConversation).reduce<Record<string, AsyncJobConversationSummary>>((summaries, [conversationId, messages]) => {
    const summary = buildAsyncJobSummary(messages, conversationId);
    if (summary) {
      summaries[conversationId] = summary;
    }
    return summaries;
  }, {});
}

export function mergePendingAsyncJobPlaceholders(serverMessages: Message[], previousMessages: Message[]) {
  const existingIds = new Set(serverMessages.map((message) => message.id));
  const pendingPlaceholders = getPendingAsyncJobPlaceholderMessages(previousMessages).filter((message) => !existingIds.has(message.id));

  return [...serverMessages, ...pendingPlaceholders].sort(
    (left, right) => new Date(getMessageTimestamp(left)).getTime() - new Date(getMessageTimestamp(right)).getTime(),
  );
}
